import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Lock, PanelLeftOpen, PanelLeftClose, ShieldCheck,
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import PlanChip from '@/components/dashboard/PlanChip';

/**
 * NavSidebar — stable TWO-COLUMN dashboard navigation (customer + admin).
 *
 * Why two columns: there are a LOT of pages. A single scrolling list stretches
 * far down; the owner explicitly prefers a short primary + a secondary panel.
 *   • Column A (primary rail, 80px) — the 6 top-level GROUPS as icons. Fixed,
 *     never scrolls. Click a group → its panel opens AND it navigates to the
 *     group's first page. Active group is highlighted.
 *   • Column B (panel, 256px) — the SELECTED group's pages only, scrolls
 *     INDEPENDENTLY (so nothing ever stretches the whole window). No truncation.
 *
 * Collapsed mode (panel hidden via the footer toggle): the primary rail stays;
 * each multi-page group opens a stable Popover; single-page groups are links.
 *
 * RBAC / onboarding / entitlement gating is applied upstream (`areas` is final).
 */
const slug = (s = '') =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function NavSidebar({
  areas = [], isActive, ja, brandTo, accent = 'customer',
  expanded = true, onToggleExpand, onInfoClick, persistKey = 'nav',
}) {
  const navigate = useNavigate();
  const isAdmin = accent === 'admin';

  // Group containing the active route (longest matching path wins).
  const activeGroupId = (() => {
    let best = null, len = -1;
    for (const a of areas) {
      for (const it of (a.items || [])) {
        if (it.path && isActive(it.path) && it.path.length > len) { best = a.id; len = it.path.length; }
      }
    }
    return best;
  })();

  // Selected group drives the panel. Follows the route, but a click can switch
  // it without leaving the page (set-state-during-render keeps them in sync).
  const [selectedId, setSelectedId] = useState(activeGroupId || areas[0]?.id);
  const [routeSnap, setRouteSnap] = useState(activeGroupId);
  if (activeGroupId && activeGroupId !== routeSnap) {
    setRouteSnap(activeGroupId);
    setSelectedId(activeGroupId);
  }

  const isHostGroup = (a) => (a.items || []).length > 0 && (a.items || []).every((it) => it.hostOnly);
  const selectedArea = areas.find((a) => a.id === selectedId) || areas.find((a) => a.id === activeGroupId) || areas[0];

  const onGroupClick = (a) => {
    setSelectedId(a.id);
    const first = a.items?.find((it) => !it.locked) || a.items?.[0];
    if (first && !first.locked) navigate(first.path);
  };

  // ── one page row (link OR locked stub) ─────────────────────────────────
  // `idx` makes the React key unique even if two items in one area ever share
  // a path (edge-state feature flags), eliminating duplicate-key warnings.
  const renderItem = (item, idx = 0) => {
    const testId = item.testId || `nav-item-${slug(item.label)}`;
    if (item.locked) {
      return (
        <div
          key={`${item.path}#${idx}`}
          data-testid={testId}
          title={ja ? 'ETC利用照会サービス（MEISAI）接続とETCカード登録が完了すると利用できます。'
                    : 'Unlocks after you complete MEISAI connection and ETC card setup.'}
          aria-label={ja ? '機能はロックされています' : 'Feature locked'}
          className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl opacity-55 cursor-not-allowed select-none"
        >
          <div className="flex items-center gap-3 min-w-0">
            <item.icon className="w-4 h-4 flex-shrink-0 text-slate-400" strokeWidth={1.75} />
            <span className="text-sm text-slate-500 truncate">{item.label}</span>
          </div>
          <Lock className="w-3 h-3 flex-shrink-0 text-slate-400" strokeWidth={2} />
        </div>
      );
    }
    const active = isActive(item.path);
    const host = item.hostOnly;
    return (
      <Link
        key={`${item.path}#${idx}`}
        to={item.path}
        data-testid={testId}
        title={item.label}
        aria-current={active ? 'page' : undefined}
        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-150 ${
          active
            ? (host ? 'bg-amber-500/10 text-amber-700 font-semibold' : 'bg-[#9200C7]/[0.08] text-[#9200C7] font-semibold')
            : 'text-slate-600 hover:text-[#9200C7] hover:bg-slate-50 font-medium'
        }`}
      >
        {active && <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-full ${host ? 'bg-amber-500' : 'bg-[#9200C7]'}`} />}
        <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
        <span className="truncate flex-1">{item.label}</span>
        {host && !active && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">HOST</span>
        )}
        {item.badge && !host && !active && (
          <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-[#9200C7]/10 text-[#9200C7]">{item.badge}</span>
        )}
      </Link>
    );
  };

  // ── primary rail group icon ────────────────────────────────────────────
  const renderRailIcon = (a) => {
    const host = isHostGroup(a);
    const groupActive = a.id === activeGroupId;
    const selected = a.id === selectedId && expanded;
    const highlight = groupActive || selected;
    return (
      <button
        type="button"
        onClick={() => onGroupClick(a)}
        data-testid={`nav-group-${a.id}`}
        aria-current={groupActive ? 'true' : undefined}
        className={`relative flex flex-col items-center justify-center gap-1 w-16 py-2 rounded-xl transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#9200C7]/40 ${
          highlight ? (host ? 'bg-amber-500/10 text-amber-600' : 'bg-[#9200C7]/10 text-[#9200C7]')
                    : 'text-slate-500 hover:text-[#9200C7] hover:bg-slate-50'
        }`}
      >
        {highlight && <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-full -ml-2 ${host ? 'bg-amber-500' : 'bg-[#9200C7]'}`} />}
        {a.icon && <a.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.9} />}
        <span className="text-[10px] font-semibold leading-tight text-center line-clamp-2 w-full px-0.5">{a.shortLabel || a.label}</span>
        {host && <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden />}
      </button>
    );
  };

  // collapsed-mode icon (with popover for multi-page groups)
  const renderCollapsedIcon = (a) => {
    const items = a.items || [];
    const host = isHostGroup(a);
    const groupActive = a.id === activeGroupId;
    const btn = (
      <button
        type="button"
        data-testid={`nav-group-${a.id}`}
        title={a.label}
        aria-label={a.label}
        onClick={items.length === 1 ? () => onGroupClick(a) : undefined}
        className={`relative flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#9200C7]/40 ${
          groupActive ? (host ? 'bg-amber-500/10 text-amber-600' : 'bg-[#9200C7]/10 text-[#9200C7]')
                      : 'text-slate-500 hover:text-[#9200C7] hover:bg-slate-50'
        }`}
      >
        {groupActive && <span className={`absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full -ml-3.5 ${host ? 'bg-amber-500' : 'bg-[#9200C7]'}`} />}
        {a.icon && <a.icon className="w-5 h-5" strokeWidth={1.9} />}
        {host && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden />}
      </button>
    );
    if (items.length === 1) return <div key={a.id} className="w-full">{btn}</div>;
    return (
      <Popover key={a.id}>
        <PopoverTrigger asChild>{btn}</PopoverTrigger>
        <PopoverContent side="right" align="start" sideOffset={10} className="w-60 p-2">
          <div className="px-2 pb-2 mb-1 border-b border-slate-100 flex items-center gap-2">
            {a.icon && <a.icon className="w-4 h-4 text-[#9200C7]" strokeWidth={2} />}
            <span className="text-sm font-bold text-slate-900">{a.label}</span>
            {host && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden />}
          </div>
          <div className="space-y-0.5 max-h-[70vh] overflow-y-auto" data-testid={`nav-popover-items-${a.id}`}>
            {items.map((it, i) => renderItem(it, i))}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  const toggleLabel = expanded
    ? (ja ? 'メニューを閉じる（アイコンのみ）' : 'Hide menu panel')
    : (ja ? 'メニューを開く' : 'Show menu panel');

  return (
    <>
      {/* Column A — primary rail (always visible, never scrolls) */}
      <aside
        className="hidden md:flex flex-col bg-white border-r border-slate-200/70 fixed inset-y-0 left-0 z-40 w-20"
        data-testid="nav-primary-rail"
        data-rail-expanded={expanded ? 'true' : 'false'}
      >
        <div className="h-16 flex items-center justify-center border-b border-slate-100 flex-shrink-0">
          <Link to={brandTo} data-testid="nav-rail-brand" title="Japan Toll Receipts">
            <img src="/assets/logo/etc-lane-icon.png" alt="JTR" className="h-9 w-9 object-contain" />
          </Link>
        </div>
        <TooltipProvider delayDuration={150}>
          <nav className="flex-1 overflow-y-auto overflow-x-hidden w-full py-3 flex flex-col items-center gap-1.5" data-testid="nav-rail-areas">
            {areas.map((a) => (expanded ? (
              <Tooltip key={a.id}>
                <TooltipTrigger asChild><div className="w-full flex justify-center">{renderRailIcon(a)}</div></TooltipTrigger>
                <TooltipContent side="right"><span className="font-semibold">{a.label}</span></TooltipContent>
              </Tooltip>
            ) : renderCollapsedIcon(a)))}
          </nav>
        </TooltipProvider>
        <div className="flex-shrink-0 border-t border-slate-100">
          {onInfoClick && (
            <button
              type="button"
              onClick={onInfoClick}
              className="w-full flex items-center justify-center text-slate-500 hover:text-violet-700 hover:bg-violet-50/60 transition-colors py-2.5"
              data-testid="nav-rail-info"
              title={ja ? '情報の取り扱い' : 'How we handle your information'}
            >
              <ShieldCheck className="w-4 h-4 text-violet-500" strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            onClick={onToggleExpand}
            className="w-full flex items-center justify-center text-slate-500 hover:text-[#9200C7] hover:bg-slate-50 transition-colors py-2.5"
            data-testid="nav-rail-toggle"
            title={toggleLabel}
            aria-label={toggleLabel}
            aria-pressed={expanded}
          >
            {expanded ? <PanelLeftClose className="w-[18px] h-[18px]" strokeWidth={1.9} />
                      : <PanelLeftOpen className="w-[18px] h-[18px]" strokeWidth={1.9} />}
          </button>
          <div className={`h-1 w-full ${isAdmin ? 'bg-gradient-to-r from-violet-500/40 to-indigo-500/40' : 'bg-[#9200C7]/20'}`} />
        </div>
      </aside>

      {/* Column B — docked panel for the selected group (scrolls on its own) */}
      {expanded && selectedArea && (
        <aside
          className="hidden md:flex flex-col bg-white border-r border-slate-200/70 fixed inset-y-0 z-30 w-64"
          style={{ left: '80px' }}
          data-testid="nav-contextual-sidebar"
        >
          <div className="h-16 flex items-center gap-2 px-4 border-b border-slate-100 flex-shrink-0">
            {selectedArea.icon && <selectedArea.icon className="w-4 h-4 text-[#9200C7] flex-shrink-0" strokeWidth={2} />}
            <span className="text-sm font-bold text-slate-900" data-testid="nav-contextual-title">{selectedArea.label}</span>
            {isHostGroup(selectedArea) && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">HOST</span>
            )}
          </div>
          <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-0.5" data-testid="nav-contextual-items">
            {(selectedArea.items || []).map((it, i) => renderItem(it, i))}
          </nav>
          <div className="flex-shrink-0 border-t border-slate-100 p-3">
            <PlanChip />
          </div>
        </aside>
      )}
    </>
  );
}
